import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlternarDaBarra, BarraDeLista, BuscaDaBarra, FiltrosAtivos, RodapeDaLista, usePaginacao } from '../../BarraDeLista';
import { dataHora, nomeDoContato } from '../../formato';
import { useSessao } from '../../sessao';
import { degrauDe, SITUACAO_DA_TAREFA } from '../../situacoes';
import { AtualizacaoPausada, useTempoReal } from '../../tempoReal';
import { Carregando, ErroDaApi, RespostaDaAcao, Selo, Vazio } from '../../ui';
import { api } from '../../api';
import { TIPO_DE_TAREFA, textoDe, type Tarefa } from './crm';
import { JanelaCancelarTarefa } from './JanelasDaOportunidade';
import './oportunidades.css';

/**
 * As tarefas do Comercial — onda 550 (Plano 2 · Comercial, Etapa 1, Tarefa
 * 15; spec §5.1). `/comercial/tarefas`.
 *
 * O que alguém precisa fazer por um pedido: ligar, retornar, acompanhar. As
 * minhas primeiro (o recorte nasce ligado), as vencidas à vista com o selo
 * «Vencida»; «Feita» tira da lista na hora, e cancelar pede o motivo (10
 * caracteres ou mais, P-TXT-10). As automáticas do acompanhamento da
 * proposta (D+2, D+7 e 2 dias antes de vencer) entram na Tarefa 24.
 */

const semAcento = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function Tarefas() {
  const { pode } = useSessao();
  const [busca, setBusca] = useSearchParams();
  const minhas = busca.get('minhas') !== '0';
  const vencidas = busca.get('vencidas') === '1';
  const [texto, setTexto] = useState('');
  const [lista, setLista] = useState<Tarefa[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [acaoErro, setAcaoErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [cancelando, setCancelando] = useState<Tarefa | null>(null);
  const pedido = useRef(0);

  const mudar = (chave: 'minhas' | 'vencidas', valor: string | null) => {
    const b = new URLSearchParams(busca);
    if (valor === null) b.delete(chave); else b.set(chave, valor);
    setBusca(b, { replace: true });
  };

  const buscar = useCallback(async () => {
    const n = ++pedido.current;
    const q = new URLSearchParams();
    if (minhas) q.set('minhas', '1');
    if (vencidas) q.set('vencidas', '1');
    try {
      const r = await api<Tarefa[]>(`/comercial/tarefas?${q}`);
      if (n !== pedido.current) return;
      setLista(r);
      setErro(null);
    } catch (e) {
      if (n === pedido.current) setErro(e);
    }
  }, [minhas, vencidas]);
  useEffect(() => { void buscar(); }, [buscar]);

  const adiada = useRef<number | null>(null);
  const tempoReal = useTempoReal(['comercial.funil'], () => {
    if (adiada.current !== null) return;
    adiada.current = window.setTimeout(() => { adiada.current = null; void buscar(); }, 300);
  });
  useEffect(() => () => { if (adiada.current !== null) window.clearTimeout(adiada.current); }, []);

  const visiveis = useMemo(() => {
    const q = semAcento(texto.trim());
    return (lista ?? []).filter((t) => !q || semAcento(
      `${t.titulo} ${t.oportunidade?.contato ?? ''} ${t.conversa?.contato ?? ''}`).includes(q));
  }, [lista, texto]);
  const pag = usePaginacao(visiveis, 50, `${texto}|${minhas}|${vencidas}`);
  const contaVencidas = (lista ?? []).filter((t) => t.vencida).length;

  const feita = async (t: Tarefa) => {
    setOk(null);
    setAcaoErro(null);
    try {
      await api(`/comercial/tarefas/${t.id}`, { metodo: 'PATCH', corpo: { situacao: 'feita' } });
      setLista((l) => (l ?? []).filter((x) => x.id !== t.id));
      setOk(`«${t.titulo}» feita.`);
    } catch (e) { setAcaoErro(e); }
  };

  return (
    <div className="op-lista-tela">
      <header className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Tarefas</h1>
          <p className="sub">O que falta fazer pelos pedidos, da que vence primeiro à última.</p>
        </div>
      </header>
      <BarraDeLista rotulo="Filtros das tarefas" ativos={(minhas ? 0 : 1) + (vencidas ? 1 : 0)} total={visiveis.length}
        aoLimpar={() => setBusca(new URLSearchParams(), { replace: true })}>
        <BuscaDaBarra valor={texto} aoMudar={setTexto} placeholder="Tarefa ou contato" rotulo="Buscar nas tarefas" />
        <AlternarDaBarra ligado={minhas} aoMudar={(v) => mudar('minhas', v ? null : '0')}>Minhas</AlternarDaBarra>
        <AlternarDaBarra ligado={vencidas} aoMudar={(v) => mudar('vencidas', v ? '1' : null)}>
          Vencidas{contaVencidas && !vencidas ? ` (${contaVencidas})` : ''}
        </AlternarDaBarra>
      </BarraDeLista>
      <FiltrosAtivos itens={texto.trim() ? [{ chave: 'busca', texto: `«${texto.trim()}»`, tirar: () => setTexto('') }] : []}
        aoLimpar={() => setTexto('')}
        contagem={lista ? `${visiveis.length} ${visiveis.length === 1 ? 'tarefa aberta' : 'tarefas abertas'}` : undefined} />
      <AtualizacaoPausada estado={tempoReal} />
      <RespostaDaAcao erro={acaoErro} ok={ok} />

      {erro && !lista ? <ErroDaApi erro={erro} />
        : !lista ? <Carregando texto="Carregando as tarefas" />
          : visiveis.length === 0 ? (
            <Vazio>{vencidas ? 'Nenhuma tarefa vencida. Em dia.' : 'Nenhuma tarefa aberta aqui.'}</Vazio>
          ) : (
            <div className="cartao op-lista">
              <ul className="tarefas-lista">
                {pag.visiveis.map((t) => {
                  const sit = degrauDe(SITUACAO_DA_TAREFA, t.vencida ? 'vencida' : t.situacao);
                  return (
                    <li key={t.id} className={`tarefa-linha${t.vencida ? ' vencida' : ''}`}>
                      <div className="tarefa-texto">
                        <span className="op-tarefa-titulo">{t.titulo}</span>
                        <span className="op-aux">
                          {textoDe(TIPO_DE_TAREFA, t.tipo)} · vence {dataHora(t.venceEm)}
                          {!minhas && ` · ${t.responsavel.tipo === 'agente' ? 'o agente' : t.responsavel.nome}`}
                        </span>
                        {t.oportunidade ? (
                          <Link to={`/comercial/oportunidade/${t.oportunidade.id}`}>{nomeDoContato(t.oportunidade.contato)}</Link>
                        ) : t.conversa && (
                          <Link to={`/comercial/conversa/${t.conversa.id}`}>{nomeDoContato(t.conversa.contato)} · {t.conversa.canal}</Link>
                        )}
                      </div>
                      <Selo tom={sit.tom}>{sit.texto}</Selo>
                      {pode('crm.editar') && (
                        <span className="op-tarefa-acoes">
                          <button type="button" className="botao secundario" onClick={() => void feita(t)}>Feita</button>
                          <button type="button" className="botao discreto" onClick={() => setCancelando(t)}>Cancelar…</button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
              <RodapeDaLista {...pag} teto={500} />
            </div>
          )}

      <JanelaCancelarTarefa aberta={!!cancelando} tarefa={cancelando} aoFechar={() => setCancelando(null)}
        aoGravar={() => {
          const t = cancelando;
          setCancelando(null);
          setLista((l) => (l ?? []).filter((x) => x.id !== t?.id));
          setOk('Tarefa cancelada.');
        }} />
    </div>
  );
}
